import { Swiper, A11y, Navigation, Pagination, Scrollbar, Autoplay } from 'swiper-modules';

class SwiperSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.swiper = this.querySelector('[data-swiper]');

    // Stop when the swiper is not found
    if (!this.swiper) return;

    // Default to improve user experience
    this.swiperOptions = {
      observer: true,
      threshold: 10,
      modules: [A11y],
      hasVideo: false,
    };

    // Check if we have extra options on the HTML
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.swiperOptions = {
        ...this.swiperOptions,
        ...dataOptions,
      };
    }

    if (this.querySelector('.video')) {
      this.swiperOptions.hasVideo = true;
    }

    // Load the Navigation, Pagination, Scrollbar, Autoplay modules as needed
    if (this.swiperOptions.pagination) this.swiperOptions.modules.push(Pagination);
    if (this.swiperOptions.navigation) this.swiperOptions.modules.push(Navigation);
    if (this.swiperOptions.scrollbar) this.swiperOptions.modules.push(Scrollbar);
    if (this.swiperOptions.autoplay) this.swiperOptions.modules.push(Autoplay);

    // render the options and init the swiper
    this.init();

    // When the swiper has video
    if (this.swiperOptions.hasVideo) {
      // when the swiper contains a video, pause it when it's swiped out
      this.swiperInstance.on('slideChangeTransitionEnd', (swiper) => {
        const previousSlide = swiper.slides[swiper.previousIndex];
        const previousVideo = previousSlide.querySelector('custom-video');

        if (previousVideo) previousVideo.pauseVideo();
      });
    }

    // Listen to extra events when in Shopify editor
    if (Shopify.designMode) {
      // Update the swiper when the section event is triggered
      window.addEventListener('shopify:section:load', event => this.init(event, true));

      // When on block select go to the slide in front-end
      window.addEventListener('shopify:block:select', event => this.handleBlockSelect(event));

      // When on block deselect go to the slide in front-end
      window.addEventListener('shopify:block:deselect', event => this.handleBlockSelect(event));
    }
  }

  disconnectedCallback() {
    this.swiperInstance?.destroy();
  }

  init(event = null, updateSwiper = false) {
    // only used for the Theme Editor to check if the swiper matches the section that was updated
    if (updateSwiper && event.detail.sectionId !== this.swiperOptions.sectionID) return;

    if (updateSwiper) {
      // Check if we have extra options on the HTML
      if (this.dataset.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.swiperOptions = {
          ...this.swiperOptions,
          ...dataOptions,
        };
      }
      this.updateSwiper();
    }
    else {
      // Call swiper with selected swiper element and options
      this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);
    }
  }

  updateSwiper() {
    this.swiperInstance.update();

    if (this.swiperOptions.navigation?.nextEl) {
      this.swiperInstance.navigation.init();
      this.swiperInstance.navigation.update();
    }
    if (this.swiperOptions.pagination?.el) {
      this.swiperInstance.pagination.init();
      this.swiperInstance.pagination.render();
    }
    // SCROLLBAR doesn't update in the theme editor. This seems to be a bug in swiper.
    if (this.swiperOptions.scrollbar?.el) {
      this.swiperInstance.scrollbar.init();
      this.swiperInstance.scrollbar.updateSize();
      this.swiperInstance.scrollbar.setTranslate();
    }
  }

  /**
   * Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect(event) {
    if (event.detail.sectionId !== this.swiperOptions.sectionID) return;

    // Check if the slide index is set
    if (!('swiperSlideIndex' in event.target.dataset)) return;

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = +event.target.dataset.swiperSlideIndex;

    // Slide to slide based on the data attribute from the target
    const swiperToUpdate = event.target.closest('[data-swiper]');

    swiperToUpdate.swiper.slideTo(swipeToSlideIndex, 500, false);
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}

class ProductRecommendations extends SwiperSlider {
  constructor() {
    super();

    if (this.querySelector('swiper-slider')) {
      const dataOptions = this.querySelector('swiper-slider').dataset.options;
      // Check if we have extra options on the HTML
      if (dataOptions) {
        const options = JSON.parse(dataOptions);
        if (options) {
          this.swiperOptions = {
            ...this.swiperOptions,
            ...options,
          };
        }
      }
    }

    this.init();
  }

  init() {
    if (this.dataset && this.dataset.url) {
      fetch(this.dataset.url)
        .then(response => response.text())
        .then((text) => {
          const html = document.createRange().createContextualFragment(text).querySelector('[data-recommended-products]').innerHTML;

          this.querySelector('[data-recommended-products]').innerHTML = html;

          window.initDialogs(this);
        })
        .catch((event) => {
          debug() && console.error(event);
        });
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
